import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert, Card } from '../../components/ui/display';
import { Checkbox, Field } from '../../components/ui/inputs';
import { ConfirmDialog } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { DataTable } from '../../tables/DataTable';
import type { FxRecalculationResult } from '../../types/models';
import { formatDate, formatDecimal, formatMoney, monthName } from '../../utils/format';

/**
 * Admin tool: re-apply the current monthly rate to revenue already saved in
 * that month. Always previews first; applying is audited per entry.
 */
export function RecalculateCard({ year, currencies }: { year: number; currencies: string[] }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [currencyCode, setCurrencyCode] = useState(currencies[0] ?? '');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [includeManual, setIncludeManual] = useState(false);
  const [preview, setPreview] = useState<FxRecalculationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const run = async (dryRun: boolean) => {
    setBusy(true);
    try {
      const res = await api.post<FxRecalculationResult>('/currency-rates/recalculate', {
        currencyCode,
        year,
        month: Number(month),
        includeManual,
        dryRun,
      });
      setPreview(res);
      if (!dryRun) {
        toast.success(
          `Recalculated ${res.affected} revenue entr${res.affected === 1 ? 'y' : 'ies'}`,
        );
        setConfirming(false);
        for (const key of ['revenue', 'dashboard', 'morning-entry'])
          void queryClient.invalidateQueries({ queryKey: [key] });
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.errors[0]?.message ?? err.message) : 'Recalculation failed',
      );
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className="mt-6"
      title={
        <span className="flex items-center gap-2">
          <RefreshCw className="size-4 text-slate-400" /> Recalculate past revenue
        </span>
      }
      description="Only needed if a monthly rate was corrected after revenue was entered. Preview first; every change is recorded in the audit log."
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Currency" className="w-36">
          <SearchableSelect
            ariaLabel="Currency"
            value={currencyCode}
            options={currencies.map((c) => ({ value: c, label: c }))}
            onChange={(v) => {
              setCurrencyCode(v);
              setPreview(null);
            }}
          />
        </Field>
        <Field label={`Month (${year})`} className="w-44">
          <SearchableSelect
            ariaLabel="Month"
            value={month}
            options={Array.from({ length: 12 }, (_, i) => ({
              value: String(i + 1),
              label: monthName(i + 1, true),
            }))}
            onChange={(v) => {
              setMonth(v);
              setPreview(null);
            }}
          />
        </Field>
        <Checkbox
          label="Also re-rate entries saved with a manual rate"
          checked={includeManual}
          onChange={(e) => {
            setIncludeManual(e.target.checked);
            setPreview(null);
          }}
          className="pb-2.5"
        />
        <Button
          variant="secondary"
          loading={busy && !confirming}
          disabled={!currencyCode}
          onClick={() => void run(true)}
        >
          Preview
        </Button>
      </div>

      {preview && !preview.applied && (
        <div className="mt-5 space-y-3">
          {preview.affected === 0 ? (
            <Alert tone="success">
              All {preview.currencyCode} revenue for {monthName(preview.month, true)} {preview.year}{' '}
              already uses ₹{formatDecimal(preview.newRate)}.
              {preview.manualSkipped > 0 &&
                ` ${preview.manualSkipped} manual-rate entr${preview.manualSkipped === 1 ? 'y was' : 'ies were'} left unchanged.`}
            </Alert>
          ) : (
            <>
              <Alert
                tone="warning"
                title={`${preview.affected} entr${preview.affected === 1 ? 'y' : 'ies'} would change to ₹${formatDecimal(preview.newRate)}`}
              >
                Revenue in INR changes by{' '}
                <strong className="num">{formatMoney(preview.totalDeltaInr)}</strong>.
                {preview.manualSkipped > 0 &&
                  ` ${preview.manualSkipped} manual-rate entr${preview.manualSkipped === 1 ? 'y is' : 'ies are'} skipped.`}
              </Alert>
              <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200">
                <DataTable
                  tableId="recalculate"
                  rows={preview.rows}
                  rowKey={(r) => r.id}
                  columns={[
                    { key: 'date', header: 'Date', render: (r) => formatDate(r.transactionDate) },
                    { key: 'account', header: 'Account', render: (r) => `${r.app} — ${r.account}` },
                    {
                      key: 'amount',
                      header: 'Revenue',
                      align: 'right',
                      render: (r) => formatMoney(r.revenueAmount, preview.currencyCode),
                    },
                    {
                      key: 'old',
                      header: 'Old rate',
                      align: 'right',
                      render: (r) =>
                        `₹${formatDecimal(r.oldRate)}${r.oldSource === 'MANUAL' ? ' (manual)' : ''}`,
                    },
                    {
                      key: 'oldInr',
                      header: 'Old INR',
                      align: 'right',
                      render: (r) => formatMoney(r.oldInr),
                    },
                    {
                      key: 'newInr',
                      header: 'New INR',
                      align: 'right',
                      render: (r) => formatMoney(r.newInr),
                    },
                    {
                      key: 'delta',
                      header: 'Change',
                      align: 'right',
                      render: (r) => formatMoney(r.delta),
                    },
                  ]}
                />
              </div>
              {preview.affected > preview.rows.length && (
                <p className="text-xs text-slate-500">
                  Showing the first {preview.rows.length} of {preview.affected} entries.
                </p>
              )}
              <Button onClick={() => setConfirming(true)}>
                Apply to {preview.affected} entries
              </Button>
            </>
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirming}
        tone="danger"
        title="Change historical revenue?"
        confirmLabel="Recalculate"
        loading={busy}
        message={
          preview &&
          `${preview.affected} revenue entries for ${monthName(preview.month, true)} ${preview.year} will use ₹${formatDecimal(preview.newRate)} per ${preview.currencyCode}. INR revenue changes by ${formatMoney(preview.totalDeltaInr)}. This is recorded in the audit log.`
        }
        onConfirm={() => void run(false)}
        onCancel={() => setConfirming(false)}
      />
    </Card>
  );
}
